
import { useState } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

export default function OrderSection() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    direccion: '',
    localidad: '',
    productos: '',
    cantidad: '',
    observaciones: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const localidades = [
    'Usaquén', 'Chapinero', 'Santa Fe', 'San Cristóbal', 'Usme', 'Tunjuelito',
    'Bosa', 'Kennedy', 'Fontibón', 'Engativá', 'Suba', 'Barrios Unidos',
    'Teusaquillo', 'Los Mártires', 'Antonio Nariño', 'Puente Aranda',
    'La Candelaria', 'Rafael Uribe Uribe', 'Ciudad Bolívar', 'Sumapaz'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validación básica
    if (!formData.nombre || !formData.telefono || !formData.direccion || !formData.localidad || !formData.productos) {
      alert('Por favor completa todos los campos obligatorios');
      setIsSubmitting(false);
      return;
    }

    if (formData.observaciones.length > 500) {
      alert('Las observaciones no pueden exceder 500 caracteres');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3jcujt4ikgvg63iuln0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          direccion: '',
          localidad: '',
          productos: '',
          cantidad: '',
          observaciones: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="pedidos" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Hacer Pedido
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Completa el formulario y recibe tu pedido en 30-60 minutos. 
            Nuestro equipo se pondrá en contacto contigo para confirmar los detalles.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="p-8">
            <form id="pedidos-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="+57 301 234 5678"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección Completa *
                  </label>
                  <input
                    type="text"
                    id="direccion"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Calle 123 #45-67, Apto 101"
                  />
                </div>

                <div>
                  <label htmlFor="localidad" className="block text-sm font-medium text-gray-700 mb-2">
                    Localidad *
                  </label>
                  <div className="relative">
                    <select
                      id="localidad"
                      name="localidad"
                      value={formData.localidad}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Selecciona tu localidad</option>
                      {localidades.map((localidad) => (
                        <option key={localidad} value={localidad}>
                          {localidad}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="productos" className="block text-sm font-medium text-gray-700 mb-2">
                    Productos Deseados *
                  </label>
                  <input
                    type="text"
                    id="productos"
                    name="productos"
                    value={formData.productos}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Ej: Whisky Johnnie Walker Red Label, Cerveza Corona"
                  />
                </div>

                <div>
                  <label htmlFor="cantidad" className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad
                  </label>
                  <input
                    type="text"
                    id="cantidad"
                    name="cantidad"
                    value={formData.cantidad}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Ej: 1 botella, 6 cervezas"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones Adicionales
                </label>
                <textarea
                  id="observaciones"
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                  placeholder="Instrucciones especiales, referencias del lugar, etc. (máximo 500 caracteres)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.observaciones.length}/500 caracteres
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-information-fill text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-yellow-800 mb-1">Información Importante</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Tiempo de entrega: 30-60 minutos</li>
                      <li>• Pago contraentrega disponible</li>
                      <li>• Verificaremos disponibilidad y precios antes de confirmar</li>
                      <li>• Domicilio gratuito en pedidos superiores a $80.000</li>
                    </ul>
                  </div>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-fill text-green-600"></i>
                    </div>
                    <p className="text-green-800 font-medium">
                      ¡Pedido enviado exitosamente! Nos pondremos en contacto contigo pronto.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-error-warning-fill text-red-600"></i>
                    </div>
                    <p className="text-red-800 font-medium">
                      Error al enviar el pedido. Por favor intenta nuevamente.
                    </p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full text-lg py-4"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </div>
                    Enviando Pedido...
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-send-plane-fill"></i>
                    </div>
                    Enviar Pedido
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
}
